/**
 * MinionType — Type classifications for minions and abilities.
 *
 * Minions have exactly ONE type. Abilities can have MULTIPLE types
 * (e.g., an ability could be both "Healer" and "Bard").
 *
 * Types:
 * - warrior: Melee fighters, high attack, moderate HP
 * - mage: Spellcasters, high attack, low HP
 * - healer: Support minions that restore HP
 * - tank: Defensive minions, high HP, low attack
 * - bard: Buff/support minions that enhance allies
 * - undead: Temporary combat creatures spawned by the Necromancy ability.
 *   Never drawn from the card pool; only exists during combat.
 * - blightcaster / buffer / runekeeper: LOCKED classes — they exist in the
 *   shared model (definitions in testMinions.ts) but are excluded from the
 *   draw pool and have NO acquisition path yet (like the Undead card).
 * - swarmer: LOCKED class — excluded from the draw pool (like the Undead
 *   card). It reaches the board ONLY as a temporary combat token: the
 *   SwarmLord ability (Mage/Blightcaster, level 5) fills the owner's board
 *   with Swarmers when the holder dies. Every Swarmer starts with the
 *   Glasscannon keyword (it dies after it has attacked).
 * - item: ITEM class — items are class cards that CAN be on the battlefield
 *   (a future acquisition path will place them) but are excluded from the
 *   draw pool. They NEVER attack (they always skip their attack — the
 *   RoundCoordinator's attacker loop), always stay at level 1 and can never
 *   be upgraded, can deal counter damage like regular minions, CAN carry
 *   keywords (e.g. Treasure / Life-full / Explosive), render with a light
 *   yellow class color and a PER-CARD image (each item has its own fixed
 *   art keyed by minion ID — the user cannot change an item's skin), and
 *   appear in NO skin menu (they have no SkinDefinition entries).
 * - spell: SPELL class — instant cards. A spell looks like a minion card
 *   (name + class badge) but has NO attack and NO health: the card's center
 *   carries its effect text. It occupies a hand slot, can be sold (its own
 *   fixed gold value — not level-based) and can be given to a COOP teammate
 *   like a minion. It is PLAYED by CASTING: the effect goes into play
 *   immediately and the card despawns — it NEVER lands on the battlefield
 *   (no level/upgrade/keyword lifecycle). Targeting: 'minion' spells are
 *   cast by dragging the card onto a friendly battlefield minion,
 *   'battlefield' spells by releasing anywhere over the battlefield.
 *   LOCKED (no acquisition path yet — see SpellDefinitions.ts): the
 *   definitions exist in the shared model but the spells are NOT in the
 *   draw pool and can never be drawn or gotten for now.
 * - spellmaker: SPELLMAKER class — baby blue, 10/1. LOCKED: excluded from
 *   the draw pool; it reaches the board via the Healer TRANSFORM (the
 *   to_spellmaker transform, level 3). Every Spellmaker starts with the
 *   Spellmaker keyword (adds spells to the draw pile — see
 *   AbilityConstants) and can learn SpellMaster at level 5 (it REPLACES
 *   Spellmaker and counts twice).
 * TASK 204: the accidental `spawner` class (a Swarmer misspelling — it
 *   appeared in the skins menu) is REMOVED. The Infestation spell's combat
 *   token is a SWARMER (a Fragile Swarmer — see applyInfestation) with the
 *   `infestation_` token-ID prefix; it reaches the board ONLY mid-combat
 *   and is discarded when combat ends (battlefield restoration).
 */

export type MinionType =
  | 'warrior'
  | 'mage'
  | 'healer'
  | 'tank'
  | 'bard'
  | 'undead'
  | 'blightcaster'
  | 'buffer'
  | 'runekeeper'
  | 'swarmer'
  | 'item'
  | 'spell'
  | 'spellmaker';

/** All valid minion type values. */
export const VALID_MINION_TYPES: ReadonlyArray<MinionType> = Object.freeze([
  'warrior',
  'mage',
  'healer',
  'tank',
  'bard',
  'undead',
  'blightcaster',
  'buffer',
  'runekeeper',
  'swarmer',
  'item',
  'spell',
  'spellmaker',
]);

/**
 * Checks whether a string is a valid minion type.
 */
export function isValidMinionType(type: string): type is MinionType {
  return VALID_MINION_TYPES.includes(type as MinionType);
}

/**
 * Human-readable display names for each type.
 */
export const MINION_TYPE_NAMES: Record<MinionType, string> = {
  warrior: 'Warrior',
  mage: 'Mage',
  healer: 'Healer',
  tank: 'Tank',
  bard: 'Bard',
  undead: 'Undead',
  blightcaster: 'Blightcaster',
  buffer: 'Buffer',
  runekeeper: 'Runekeeper',
  swarmer: 'Swarmer',
  item: 'Item',
  spell: 'Spell',
  spellmaker: 'Spellmaker',
};

/**
 * CSS color classes for each type (used in card rendering).
 */
export const MINION_TYPE_COLORS: Record<MinionType, string> = {
  warrior: '#e74c3c',      // Red
  mage: '#3498db',         // Blue
  healer: '#2ecc71',       // Green
  tank: '#95a5a6',         // Gray
  bard: '#9b59b6',         // Purple
  undead: '#16a085',       // Dark teal (sickly shade)
  blightcaster: '#9acd32', // Slime green
  buffer: '#f1c40f',       // Yellow
  runekeeper: '#607d8b',   // Tile blue-gray (slate stone)
  swarmer: '#8B4513',      // Brown
  item: '#f5d76e',         // Light yellow (all items share the class color)
  spell: '#6c5ce7',        // Deep violet (all spells share the class color)
  spellmaker: '#87CEEB',   // Baby blue (all Spellmakers share the class color)
};

/**
 * Emoji icons for each type (used in card rendering).
 */
export const MINION_TYPE_ICONS: Record<MinionType, string> = {
  warrior: '⚔️',
  mage: '🔮',
  healer: '💚',
  tank: '🛡️',
  bard: '🎵',
  undead: '💀',
  blightcaster: '☣️',
  buffer: '💪',
  runekeeper: '🔯',
  swarmer: '🐝',
  item: '📦',
  spell: '✨',
  spellmaker: '📜',
};
